import { useState } from "react";
import { Sparkles } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { KpiCard } from "@/components/KpiCard";
import { Panel } from "@/components/Panel";
import { LeadDrilldownModal, StatusBadge, formatSegment, getLeadStageBucket } from "@/components/LeadDrilldownModal";
import { cn } from "@/lib/cn";
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
  formatPercentRaw,
  safeRatio,
} from "@/lib/format";
import type {
  MetaCampaign,
  SourceLead,
  SourceLeadsBreakdown,
  SourceRow,
} from "@/lib/types";
import { usePayload } from "@/lib/usePayload";

export function AiWebsiteBuilderLeads() {
  const { payload, isLoading, viewMode, monthLabel, monthKey } = usePayload();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [modalStage, setModalStage] = useState<string | null>(null);

  if (isLoading) {
    return <div className="p-8 text-center text-slate-500">Loading AI Website Builder metrics...</div>;
  }

  if (!payload) {
    return <EmptyState title="No metrics data" hint="Unable to load report payload." />;
  }

  // Filter breakdown to ONLY AI Website Builder sources
  const breakdown: SourceLeadsBreakdown = {};

  if (payload.source_leads_breakdown) {
    const targetMonthPrefix = monthKey ? monthKey.replace(/\//g, "-") : "";
    Object.entries(payload.source_leads_breakdown as Record<string, any[]>).forEach(([source, leads]) => {
      leads.forEach((lead) => {
        const leadSourceRaw = (lead.lead_source || "").trim().toLowerCase();
        const originalSource = source.trim().toLowerCase();

        const isSignUp = leadSourceRaw === "ai website builder sign-up" || originalSource === "ai website builder sign-up";

        if (isSignUp) {
          // Additional safety check on frontend: ensure lead/contact date matches the selected month if date exists
          const leadDate = (lead.received_date || lead.created_time || "").trim();
          if (targetMonthPrefix && leadDate && !leadDate.startsWith(targetMonthPrefix)) {
            return;
          }

          const groupName = "AI Website Builder Sign-Up";

          if (!breakdown[groupName]) {
            breakdown[groupName] = [];
          }
          breakdown[groupName].push(lead);
        }
      });
    });
  }

  // Cumulative Funnel hierarchy:
  // All leads are MQLs; Wins passed through SQO & SQL; SQOs passed through SQL.
  const t = {
    mql: 0,
    sql: 0,
    sqo: 0,
    wins: 0,
    revenue: 0,
  };

  Object.values(breakdown).forEach((leads) => {
    leads.forEach((lead) => {
      const bucket = getLeadStageBucket(lead);
      t.mql++;
      if (bucket === "SQL" || bucket === "SQO" || bucket === "Win") t.sql++;
      if (bucket === "SQO" || bucket === "Win") t.sqo++;
      if (bucket === "Win") {
        t.wins++;
        t.revenue += (lead as any).revenue || 0;
      }
    });
  });

  // Build summary table rows with cumulative counts
  const rows: SourceRow[] = Object.entries(breakdown).map(([source, leads]) => {
    const rowMql = leads.length;
    const rowSql = leads.filter((l) => {
      const b = getLeadStageBucket(l);
      return b === "SQL" || b === "SQO" || b === "Win";
    }).length;
    const rowSqo = leads.filter((l) => {
      const b = getLeadStageBucket(l);
      return b === "SQO" || b === "Win";
    }).length;
    const rowWins = leads.filter((l) => getLeadStageBucket(l) === "Win").length;
    const rowRevenue = leads.filter((l) => getLeadStageBucket(l) === "Win").reduce((acc, l) => acc + ((l as any).revenue || 0), 0);
    return {
      source,
      mql: rowMql,
      sql: rowSql,
      sqo: rowSqo,
      wins: rowWins,
      revenue: rowRevenue,
    };
  }).sort((a, b) => b.mql - a.mql);

  const hasAnyBreakdown = Object.values(breakdown).some((list) => list && list.length > 0);
  const displayLabel = viewMode === "current_week" ? (payload.weekly_reporting?.week_label || "Current Week") : monthLabel;

  // Extract Builder Meta campaigns and calculate spend & unit cost CAC metrics
  const isWeekMode = viewMode === "current_week";
  let metaSource: any[] = [];
  if (isWeekMode && payload.weekly_reporting?.meta_df) {
    metaSource = payload.weekly_reporting.meta_df;
  } else if (payload.meta_campaigns) {
    metaSource = payload.meta_campaigns;
  } else if (payload.meta_df) {
    metaSource = payload.meta_df;
  }

  const builderCampaigns: MetaCampaign[] = metaSource
    .filter((c: any) => {
      const name = String(c.campaign_name || c["Campaign Name"] || c.name || "").toLowerCase();
      return name.includes("builder");
    })
    .map((c: any) => {
      const spend = Number(c.spend ?? c["Spend"] ?? 0) || 0;
      const results = Number(c.results ?? c["Results"] ?? c.conversions ?? 0) || 0;
      const impressions = Number(c.impressions ?? c["Impressions"] ?? 0) || 0;
      const clicks = Number(c.clicks ?? c["Clicks"] ?? 0) || 0;
      const campaign_name = String(c.campaign_name || c["Campaign Name"] || c.name || "Builder Campaign");
      const effective_status = String(c.effective_status || c["Status"] || c.status || "ACTIVE");
      const objective = String(c.objective || c["Objective"] || "OUTCOME_LEADS");
      const cost_per_result = c.cost_per_result != null ? Number(c.cost_per_result) : (results > 0 ? spend / results : null);
      return {
        campaign_name,
        spend,
        results,
        impressions,
        clicks,
        effective_status,
        objective,
        cost_per_result,
      };
    });

  const builderMetaSpend = builderCampaigns.reduce((s, c) => s + c.spend, 0);

  const toggle = (source: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  };

  const collapseAll = () => setExpanded(new Set());

  const allLeads = Object.entries(breakdown).flatMap(([source, rows]) =>
    rows.map((row) => ({ ...row, source }))
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="font-display text-xl sm:text-2xl font-semibold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-purple-600" />
          AI Website Builder Leads
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
          Dedicated CRM lead breakdown & Meta Ad efficiency for "AI Website Builder Sign-Up" & "AI Website Builder Email Entry" — {displayLabel}
        </p>
      </div>

      {/* CRM Funnel Overview */}
      <div className="space-y-2">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">CRM Funnel Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <KpiCard accent="blue" label="Total MQL" value={formatNumber(t.mql)} onClick={() => setModalStage("MQL")} />
          <KpiCard accent="cyan" label="Total SQL" value={formatNumber(t.sql)} onClick={() => setModalStage("SQL")} />
          <KpiCard accent="teal" label="Total SQO" value={formatNumber(t.sqo)} onClick={() => setModalStage("SQO")} />
          <KpiCard accent="green" label="Total Wins" value={formatNumber(t.wins)} onClick={() => setModalStage("Win")} />
          <KpiCard accent="green" label="Total Revenue" value={formatCurrency(t.revenue, { compact: true })} />
        </div>
      </div>

      <Panel
        title="Leads by AI Builder Source"
        subtitle={hasAnyBreakdown ? "Click a row to expand individual leads" : "No AI Website Builder leads recorded for this period"}
        right={
          hasAnyBreakdown && expanded.size > 0 ? (
            <button
              type="button"
              onClick={collapseAll}
              className="text-xs px-2.5 py-1 rounded-md border border-border text-slate-600 hover:bg-slate-50"
            >
              Collapse all
            </button>
          ) : null
        }
      >
        <SourceTable
          rows={rows}
          breakdown={breakdown}
          expanded={expanded}
          onToggle={toggle}
        />
      </Panel>

      {/* Builder Meta Campaigns Breakdown */}
      <Panel
        title="Builder Meta Campaigns"
        subtitle="Active Meta ad campaigns promoting AI Website Builder"
        right={
          <div className="flex items-center gap-2">
            <span className="pill bg-purple-50 border border-purple-200 text-purple-700 text-xs px-2.5 py-1 font-semibold">
              Spend: {formatCurrency(builderMetaSpend)}
            </span>
          </div>
        }
      >
        {builderCampaigns.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No Builder Meta campaigns recorded for this period.</div>
        ) : (
          <BuilderCampaignTable campaigns={builderCampaigns} />
        )}
      </Panel>

      {modalStage && (
        <LeadDrilldownModal
          stage={modalStage}
          leads={allLeads}
          monthStr={monthKey}
          onClose={() => setModalStage(null)}
        />
      )}
    </div>
  );
}

interface SourceTableProps {
  rows: SourceRow[];
  breakdown: SourceLeadsBreakdown;
  expanded: Set<string>;
  onToggle: (source: string) => void;
}

function SourceTable({ rows, breakdown, expanded, onToggle }: SourceTableProps) {
  if (rows.length === 0) {
    return <div className="text-sm text-slate-500 py-6 text-center">No AI Website Builder leads found for this period.</div>;
  }

  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left label-xs border-b border-border">
            <th className="font-medium px-5 py-2.5">Source</th>
            <th className="font-medium px-3 py-2.5 text-right">MQL</th>
            <th className="font-medium px-3 py-2.5 text-right">SQL</th>
            <th className="font-medium px-3 py-2.5 text-right">SQO</th>
            <th className="font-medium px-3 py-2.5 text-right">Wins</th>
            <th className="font-medium px-3 py-2.5 text-right">Revenue</th>
            <th className="font-medium px-5 py-2.5 text-right">MQL → SQL</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const conv = safeRatio(r.sql, r.mql);
            const tone =
              conv == null
                ? "text-slate-400"
                : conv >= 0.2
                  ? "text-green-600"
                  : conv >= 0.05
                    ? "text-amber-600"
                    : "text-slate-400";
            const leads = breakdown[r.source] || [];
            const canExpand = leads.length > 0;
            const isOpen = expanded.has(r.source);
            return (
              <SourceRowGroup
                key={r.source}
                row={r}
                leads={leads}
                conv={conv}
                tone={tone}
                canExpand={canExpand}
                isOpen={isOpen}
                onToggle={() => onToggle(r.source)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SourceRowGroup({
  row,
  leads,
  conv,
  tone,
  canExpand,
  isOpen,
  onToggle,
}: {
  row: SourceRow;
  leads: SourceLead[];
  conv: number | null;
  tone: string;
  canExpand: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={canExpand ? onToggle : undefined}
        className={cn(
          "border-b border-border/50 hover:bg-slate-50/70 transition-colors",
          canExpand && "cursor-pointer"
        )}
      >
        <td className="px-5 py-3 font-medium flex items-center gap-2">
          {canExpand && (
            <span className="text-slate-400 text-xs w-3">
              {isOpen ? "▼" : "▶"}
            </span>
          )}
          <span>{row.source}</span>
        </td>
        <td className="px-3 py-3 text-right font-mono">{formatNumber(row.mql)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatNumber(row.sql)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatNumber(row.sqo)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatNumber(row.wins)}</td>
        <td className="px-3 py-3 text-right font-mono">
          {formatCurrency(row.revenue, { compact: true })}
        </td>
        <td className={cn("px-5 py-3 text-right font-mono font-medium", tone)}>
          {formatPercent(conv)}
        </td>
      </tr>

      {isOpen && (
        <tr>
          <td colSpan={7} className="bg-slate-50/70 p-4 border-b border-border">
            <div className="overflow-x-auto rounded border border-border bg-white shadow-xs">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-slate-50 text-slate-600 font-semibold">
                    <th className="px-3 py-2 text-left">Stage</th>
                    <th className="px-3 py-2 text-left">Name / Account</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Segment</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-left">Received Date</th>
                    <th className="px-3 py-2 text-left">Zoho CRM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((l) => (
                    <tr key={l.id || l.email} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <StatusBadge status={l.stage || "MQL"} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{l.name}</div>
                        {l.company && <div className="text-[10px] text-slate-500">{l.company}</div>}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{l.lead_status || "—"}</td>
                      <td className="px-3 py-2 text-slate-600">{formatSegment(l.segment)}</td>
                      <td className="px-3 py-2 text-slate-600">{l.owner || "Unassigned"}</td>
                      <td className="px-3 py-2 text-slate-600">{formatDate(l.received_date)}</td>
                      <td className="px-3 py-2">
                        {l.zoho_url ? (
                          <a
                            href={l.zoho_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-blue hover:underline font-medium"
                          >
                            Open Record →
                          </a>
                        ) : (
                          " — "
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function BuilderCampaignTable({ campaigns }: { campaigns: MetaCampaign[] }) {
  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left label-xs border-b border-border">
            <th className="font-medium px-5 py-2.5">Campaign</th>
            <th className="font-medium px-3 py-2.5">Objective</th>
            <th className="font-medium px-3 py-2.5">Status</th>
            <th className="font-medium px-3 py-2.5 text-right">Spend</th>
            <th className="font-medium px-3 py-2.5 text-right">Results</th>
            <th className="font-medium px-3 py-2.5 text-right">CPR</th>
            <th className="font-medium px-3 py-2.5 text-right">Impressions</th>
            <th className="font-medium px-3 py-2.5 text-right">Clicks</th>
            <th className="font-medium px-5 py-2.5 text-right">CTR</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => {
            const isActive = (c.effective_status || "").toUpperCase() === "ACTIVE";
            const ctr = safeRatio(c.clicks, c.impressions);
            return (
              <tr key={c.campaign_name} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                <td className="px-5 py-2.5 font-medium text-slate-900">{c.campaign_name}</td>
                <td className="px-3 py-2.5">
                  <span className="pill bg-slate-100 text-slate-700 border border-slate-200 text-[10px] tracking-wider">
                    {c.objective || "LEADS"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={cn("h-1.5 w-1.5 rounded-full", isActive ? "bg-green-500" : "bg-slate-400")} />
                    <span className={isActive ? "text-green-700 font-medium" : "text-slate-500"}>
                      {isActive ? "Active" : "Paused"}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right numeric-value font-mono">{formatCurrency(c.spend, { compact: true })}</td>
                <td className="px-3 py-2.5 text-right numeric-value font-mono">{formatNumber(c.results)}</td>
                <td className="px-3 py-2.5 text-right numeric-value font-mono">
                  {c.cost_per_result ? formatCurrency(c.cost_per_result, { compact: true }) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right numeric-value font-mono">{formatNumber(c.impressions)}</td>
                <td className="px-3 py-2.5 text-right numeric-value font-mono">{formatNumber(c.clicks)}</td>
                <td className="px-5 py-2.5 text-right numeric-value font-mono">
                  {ctr != null ? formatPercentRaw(ctr) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
